// D37 "A block always says what to do": the blocked task's translated title, plain-language explanation, evidence,
// what waits for it and the decision buttons (Kanban card, task modal), and the project attention banner (header and
// Overview). The decisions come from GET /v1/tasks/{id}/block (offered per code, with the role each needs); server
// decisions are one POST with a confirmation for the sensitive ones; client decisions navigate or open the assistant.
// The raw English reason stays under "Technical details".
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { BlockDecision, BlockDecisionOption, BlockDecisionRequest, BlockReason, ProjectSummary, ServerBlockDecision, TaskBlockView } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { decideBlock, getTaskBlock, updateTask } from "../../api/endpoints-blocks.ts";
import { getTask } from "../../api/endpoints.ts";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { Banner, ErrorBanner, localErrorMessage } from "../../components/Feedback.tsx";
import { SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { useToast } from "../../components/Toast.tsx";
import { useAssistant, type AssistantApi } from "../assistant/AssistantProvider.tsx";
import { assistantPrompt, blockExplanation, blockTitle, decisionLabel } from "./explain.ts";

const SERVER: ReadonlySet<string> = new Set(["retry_now", "accept_without_tests", "retry_same_model", "retry_other_model", "cancel_task", "override_retry", "release_dependents", "approve_cost", "reconcile_cost"]);
const DEFAULT_CAP_USD = "2.00";
// Mirror the limits of blockDecisionRequestSchema (reconciled_calls 1..50, cost_cents 0..1_000_000).
const RECONCILE_MAX_CALLS = 50;
const RECONCILE_MAX_CENTS = 1_000_000;

/** Inputs a server decision may carry; the confirmation dialogs collect them. */
type DecisionExtra = Partial<Pick<BlockDecisionRequest, "model_target_id" | "max_cost_cents" | "reconciled_calls" | "note">>;

/** Whole cents typed by the administrator: digits only, no sign and no decimals. */
function parseCents(raw: string): number | null {
  const v = raw.trim();
  if (!/^\d+$/.test(v)) return null;
  const n = Number(v);
  return n <= RECONCILE_MAX_CENTS ? n : null;
}

const usdOf = (cents: number | null) => (cents === null ? "—" : `US$ ${(cents / 100).toFixed(2)}`);

export interface BlockTask {
  id: string;
  number: number;
  title: string;
  status: string;
  version: number;
  project_id: string;
  status_reason: string | null;
  block_reason?: BlockReason | null;
}

function useAssistantSafe(): AssistantApi | null {
  try {
    return useAssistant();
  } catch {
    return null;
  }
}

/** Runs a decision: navigation, assistant, edit dialog, or a confirmed POST. Returns the dialogs to render. */
function useDecisionRunner(task: BlockTask, view: TaskBlockView | null, onChanged: () => void) {
  const { t } = useI18n();
  const { toast, announce } = useToast();
  const navigate = useNavigate();
  const assistant = useAssistantSafe();
  const [confirming, setConfirming] = useState<ServerBlockDecision | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const reason = view?.block_reason ?? task.block_reason ?? null;
  const base = `/projects/${task.project_id}`;

  const post = async (decision: ServerBlockDecision, extra: DecisionExtra = {}) => {
    setBusy(decision);
    setError(null);
    try {
      const r = await decideBlock(task.id, { decision, expected_version: view?.version ?? task.version, confirmed: true, ...extra });
      const label = decisionLabel(t, decision, reason);
      const msg = decision === "release_dependents" ? t("block.decidedReleased", { n: r.released.length, number: task.number }) : r.run_started ? t("block.decidedRun", { number: task.number, decision: label }) : t("block.decided", { number: task.number, decision: label });
      toast(msg, "success");
      announce(msg);
      setConfirming(null);
      onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const run = (option: BlockDecisionOption) => {
    const d = option.decision;
    if (SERVER.has(d)) {
      // reconcile_cost cannot be posted without its amounts and note, so it always opens its form.
      if (option.confirm || d === "reconcile_cost") setConfirming(d as ServerBlockDecision);
      else void post(d as ServerBlockDecision);
      return;
    }
    switch (d as BlockDecision) {
      case "edit_test_command":
        navigate(`${base}/settings?field=test_command`);
        return;
      case "open_settings":
      case "raise_limit":
        navigate(`${base}/settings`);
        return;
      case "open_blocking_task": {
        const first = view?.blocking[0];
        if (first) navigate(`${base}/kanban?task=${first.id}`);
        return;
      }
      case "edit_task":
        setEditing(true);
        return;
      case "split_task":
        assistant?.open({ focus: "task", prompt: assistantPrompt(t, reason, task, "split") });
        return;
      case "ask_assistant":
      default:
        assistant?.open({ focus: "task", prompt: assistantPrompt(t, reason, task) });
    }
  };

  const dialogs = (
    <>
      <ConfirmDecision
        task={task}
        view={view}
        decision={confirming === "reconcile_cost" ? null : confirming}
        busy={busy !== null}
        error={error}
        onCancel={() => {
          setConfirming(null);
          setError(null);
        }}
        onConfirm={(extra) => confirming && void post(confirming, extra)}
      />
      {confirming === "reconcile_cost" ? (
        <ReconcileCostDialog
          calls={view?.unknown_cost_calls ?? []}
          busy={busy !== null}
          error={error}
          onCancel={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={(extra) => void post("reconcile_cost", extra)}
        />
      ) : null}
      <EditTaskDialog open={editing} task={task} onClose={() => setEditing(false)} onSaved={onChanged} />
    </>
  );
  return { run, dialogs, busy, error: confirming ? null : error };
}

function ConfirmDecision({
  task,
  view,
  decision,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  task: BlockTask;
  view: TaskBlockView | null;
  decision: ServerBlockDecision | null;
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: (extra: { model_target_id?: string; max_cost_cents?: number }) => void;
}) {
  const { t } = useI18n();
  const [model, setModel] = useState("");
  const [cost, setCost] = useState(DEFAULT_CAP_USD);
  useEffect(() => {
    setModel(view?.model_targets[0]?.id ?? "");
    setCost(DEFAULT_CAP_USD);
  }, [decision, view]);
  if (!decision) return null;
  const reason = view?.block_reason ?? task.block_reason ?? null;
  const needsModel = decision === "retry_other_model";
  const needsCost = decision === "retry_same_model" || decision === "retry_other_model" || decision === "approve_cost";
  const cents = Math.round(Number(cost.replace(",", ".")) * 100);
  const costOk = !needsCost || (Number.isFinite(cents) && cents > 0);
  const usd = `US$ ${(Number.isFinite(cents) && cents > 0 ? cents / 100 : 0).toFixed(2)}`;
  return (
    <Dialog
      open
      onClose={onCancel}
      busy={busy}
      title={t("block.confirm.title", { decision: decisionLabel(t, decision, reason) })}
      testId="block-confirm"
      footer={
        <>
          <Button onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={decision === "cancel_task" ? "danger" : "primary"}
            loading={busy}
            disabled={!costOk || (needsModel && model === "")}
            onClick={() => onConfirm({ ...(needsModel ? { model_target_id: model } : {}), ...(needsCost ? { max_cost_cents: cents } : {}) })}
            data-testid="block-confirm-go"
          >
            {t("block.confirm.go")}
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>{t(`block.confirm.${decision}` as MessageKey, { number: task.number, cost: usd, n: view?.dependents.length ?? 0 })}</p>
        {needsModel ? (
          <SelectField label={t("block.confirm.model")} value={model} onChange={(e) => setModel(e.target.value)} data-testid="block-confirm-model">
            {(view?.model_targets ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </SelectField>
        ) : null}
        {needsCost ? <TextField label={t("block.confirm.cost")} hint={t("block.confirm.costHint")} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="block-confirm-cost" /> : null}
        {error ? (
          <Banner tone="error" role="alert">
            {t("block.decisionFailed", { reason: localErrorMessage(error, t) })}
          </Banner>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * reconcile_cost (D41-A3): an administrator enters the real charge of each paid call whose cost is unknown, as read on
 * the provider's records (0 when it was not charged), and names the evidence. Nothing is inferred or prefilled. The
 * form mounts when the decision opens, so every opening starts empty.
 */
function ReconcileCostDialog({
  calls,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  calls: TaskBlockView["unknown_cost_calls"];
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: (extra: DecisionExtra) => void;
}) {
  const { t, formatDateTime } = useI18n();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [attempted, setAttempted] = useState(false);
  const shown = calls.slice(0, RECONCILE_MAX_CALLS);
  const cents = shown.map((c) => parseCents(amounts[c.id] ?? ""));
  const valid = shown.length > 0 && cents.every((v) => v !== null) && note.trim() !== "";
  const total = cents.reduce<number>((sum, v) => sum + (v ?? 0), 0);

  const submit = () => {
    if (!valid) {
      setAttempted(true);
      return;
    }
    onConfirm({ reconciled_calls: shown.map((c, i) => ({ model_call_id: c.id, cost_cents: cents[i] ?? 0 })), note: note.trim() });
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      busy={busy}
      title={t("block.confirm.title", { decision: decisionLabel(t, "reconcile_cost", null) })}
      testId="block-confirm-reconcile"
      footer={
        <>
          <Button onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" loading={busy} disabled={shown.length === 0} onClick={submit} data-testid="block-confirm-go">
            {t("block.confirm.go")}
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>{t("block.confirm.reconcile_cost")}</p>
        {calls.length > RECONCILE_MAX_CALLS ? <p className="field-hint">{t("block.reconcile.batch", { max: RECONCILE_MAX_CALLS, n: calls.length })}</p> : null}
        {shown.length === 0 ? (
          <p className="subtle">{t("block.reconcile.none")}</p>
        ) : (
          <fieldset className="stack">
            <legend className="field-label">{t("block.reconcile.calls")}</legend>
            {shown.map((c, i) => {
              const call = `${c.model ?? "—"} · ${c.provider ?? "—"} · ${formatDateTime(c.created_at)}`;
              const value = cents[i] ?? null;
              return (
                <div className="stack" key={c.id}>
                  <p className="user-content">{call}</p>
                  <p className="field-hint">
                    {t("block.reconcile.callState", { state: c.state })}
                    {c.error_class ? ` · ${t("block.reconcile.callError", { error: c.error_class })}` : ""}
                  </p>
                  <TextField
                    label={t("block.reconcile.costLabel", { call })}
                    hint={t("block.reconcile.centsHelp", { usd: usdOf(value) })}
                    error={attempted && value === null ? t("block.reconcile.invalidCents") : null}
                    inputMode="numeric"
                    autoComplete="off"
                    value={amounts[c.id] ?? ""}
                    onChange={(e) => setAmounts((prev) => ({ ...prev, [c.id]: e.target.value }))}
                    data-testid="reconcile-cost-input"
                  />
                </div>
              );
            })}
          </fieldset>
        )}
        {shown.length > 0 ? (
          <p role="status" data-testid="reconcile-total">
            {t("block.reconcile.total", { cost: usdOf(total), n: shown.length })}
          </p>
        ) : null}
        <TextAreaField
          label={t("block.reconcile.note")}
          hint={t("block.reconcile.noteHelp")}
          error={attempted && note.trim() === "" ? t("block.reconcile.noteRequired") : null}
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="reconcile-note"
        />
        {attempted && !valid ? (
          <Banner tone="error" role="alert">
            {t("block.reconcile.fixFields")}
          </Banner>
        ) : null}
        {error ? (
          <Banner tone="error" role="alert">
            {t("block.decisionFailed", { reason: localErrorMessage(error, t) })}
          </Banner>
        ) : null}
      </div>
    </Dialog>
  );
}

export function EditTaskDialog({ open, task, onClose, onSaved }: { open: boolean; task: BlockTask; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState(task.version);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTitle(task.title);
    getTask(task.id)
      .then((d) => {
        setDescription(d.description);
        setVersion(d.version);
      })
      .catch(() => undefined);
  }, [open, task.id, task.title]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateTask(task.id, { expected_version: version, title: title.trim(), description });
      toast(t("editTask.saved", { number: task.number }), "success");
      onClose();
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      wide
      title={t("editTask.title", { number: task.number })}
      testId="edit-task"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" loading={busy} disabled={title.trim() === ""} onClick={() => void save()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="stack">
        <TextField label={t("editTask.name")} value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
        <TextAreaField label={t("editTask.description")} value={description} rows={8} onChange={(e) => setDescription(e.target.value)} />
        {error ? (
          <Banner tone="error" role="alert">
            {localErrorMessage(error, t)}
          </Banner>
        ) : null}
      </div>
    </Dialog>
  );
}

function DecisionButtons({ view, task, runner, limit }: { view: TaskBlockView; task: BlockTask; runner: ReturnType<typeof useDecisionRunner>; limit?: number }) {
  const { t } = useI18n();
  const reason = view.block_reason ?? task.block_reason ?? null;
  const shown = limit === undefined ? view.decisions : view.decisions.filter((d) => d.allowed).slice(0, limit);
  // The full panel also says in plain text which role a disabled decision needs (AC17), not only in the tooltip.
  const denied = limit === undefined ? shown.filter((d) => !d.allowed) : [];
  return (
    <>
      <div className="row row--wrap block-decisions" data-testid="block-decisions">
        {shown.map((d) => (
          <Button
            key={d.decision}
            size="sm"
            variant={d.decision === "cancel_task" ? "danger" : d.primary ? "primary" : "secondary"}
            disabled={!d.allowed || runner.busy !== null}
            loading={runner.busy === d.decision}
            title={d.allowed ? undefined : t("block.needsRole", { role: t(`block.role.${d.min_role}` as MessageKey) })}
            onClick={() => runner.run(d)}
            data-testid={`block-decision-${d.decision}`}
          >
            {decisionLabel(t, d.decision, reason)}
          </Button>
        ))}
      </div>
      {denied.map((d) => (
        <p key={d.decision} className="field-hint">
          {t("block.needsRoleFor", { decision: decisionLabel(t, d.decision, reason), role: t(`block.role.${d.min_role}` as MessageKey) })}
        </p>
      ))}
    </>
  );
}

/**
 * The blocked task's explanation and decisions. `compact` (Kanban card): title, explanation, waiting count, the top
 * allowed decisions and "See what to do"; full (task modal): everything, evidence and technical details included.
 */
export function BlockPanel({ task, compact = false, onChanged, onOpenDetails }: { task: BlockTask; compact?: boolean; onChanged: () => void; onOpenDetails?: () => void }) {
  const { t } = useI18n();
  const [view, setView] = useState<TaskBlockView | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getTaskBlock(task.id)
      .then((v) => !cancelled && setView(v))
      .catch((e: unknown) => !cancelled && setLoadError(e));
    return () => {
      cancelled = true;
    };
  }, [task.id, task.version, attempt]);
  const current = view && view.task_id === task.id ? view : null;
  const runner = useDecisionRunner(task, current, onChanged);
  const reason = current?.block_reason ?? task.block_reason ?? null;
  const tailLine = reason?.code === "environment" ? (reason.params.output_tail ?? current?.evidence?.output_tail ?? null) : null;
  const dependents = current?.dependents ?? [];

  if (compact) {
    return (
      <div className="block-summary" data-testid={`block-summary-${task.number}`}>
        <p className="block-summary-title">{blockTitle(t, reason)}</p>
        <p className="block-summary-body">{blockExplanation(t, reason)}</p>
        {tailLine ? <code className="block-summary-tail">{tailLine.split("\n").filter((l) => l.trim() !== "" && !l.startsWith("[exit")).at(-1)}</code> : null}
        {dependents.length > 0 ? <p className="block-summary-waiting">{t("block.dependents", { n: dependents.length })}</p> : null}
        {current ? <DecisionButtons view={current} task={task} runner={runner} limit={2} /> : null}
        {onOpenDetails ? (
          <Button size="sm" variant="ghost" onClick={onOpenDetails} data-testid={`block-open-${task.number}`}>
            {t("block.seeWhatToDo")}
          </Button>
        ) : null}
        {runner.error ? <p className="field-error">{t("block.decisionFailed", { reason: localErrorMessage(runner.error, t) })}</p> : null}
        {runner.dialogs}
      </div>
    );
  }

  return (
    <section className="block-panel stack" aria-labelledby={`block-title-${task.id}`} data-testid="block-panel">
      <div>
        <h3 id={`block-title-${task.id}`} className="block-panel-title">
          {blockTitle(t, reason)}
        </h3>
        <p className="block-panel-body">{blockExplanation(t, reason)}</p>
      </div>
      {loadError ? <ErrorBanner error={loadError} onRetry={() => setAttempt((n) => n + 1)} /> : null}
      {dependents.length > 0 ? (
        <p className="block-panel-waiting" data-testid="block-dependents">
          <strong>{t("block.dependents", { n: dependents.length })}</strong> — {t("block.dependentsList", { list: dependents.map((d) => `#${d.number}`).join(", ") })}
        </p>
      ) : null}
      {current && current.blocking.length > 0 ? <p className="block-panel-waiting">{t("block.blockedBy", { list: current.blocking.map((b) => `#${b.number}`).join(", ") })}</p> : null}
      {current ? (
        <div>
          <h4 className="detail-heading">{t("block.whatToDo")}</h4>
          <DecisionButtons view={current} task={task} runner={runner} />
        </div>
      ) : (
        <p className="subtle">{t("block.loading")}</p>
      )}
      {runner.error ? (
        <Banner tone="error" role="alert">
          {t("block.decisionFailed", { reason: localErrorMessage(runner.error, t) })}
        </Banner>
      ) : null}
      {current?.evidence || tailLine ? (
        <div>
          <h4 className="detail-heading">{t("block.evidence")}</h4>
          {current?.evidence ? <p className="subtle user-content">{current.evidence.summary}</p> : null}
          {tailLine ? (
            <>
              <p className="field-hint">{t("block.evidenceTail")}</p>
              <pre className="block-panel-tail" data-testid="block-evidence-tail">
                {tailLine}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}
      {current?.action ? (
        <div data-testid="block-action">
          <h4 className="detail-heading">{t("block.action.title")}</h4>
          <p className="subtle">{t(`block.action.owner.${current.action.owner_kind}`)}</p>
          <p className="field-hint">
            {t("block.action.next")}: <code>{current.action.next_action}</code>
          </p>
          <p className="field-hint">
            {t("block.action.resume")}: <code>{current.action.resume_condition}</code>
          </p>
        </div>
      ) : null}
      {task.status_reason ? (
        <details className="block-panel-raw">
          <summary>{t("block.technicalDetails")}</summary>
          <p className="user-content mono">{task.status_reason}</p>
        </details>
      ) : null}
      {runner.dialogs}
    </section>
  );
}

/** Project attention (header banner and Overview): the task behind it, translated, with what waits and what to do. */
export function BlockAttention({ project, testId }: { project: ProjectSummary; testId?: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const assistant = useAssistantSafe();
  const a = project.attention ?? null;
  if (!a) return null;
  const reason = a.block_reason;
  const task = { number: a.task_number, title: a.title };
  return (
    <Banner
      tone={a.status === "waiting_resource" ? "info" : "warning"}
      testId={testId ?? "project-attention"}
      title={t("block.attentionTitle", { number: a.task_number, title: blockTitle(t, reason) })}
      action={
        <div className="row row--wrap">
          <Button size="sm" variant="primary" onClick={() => navigate(`/projects/${project.id}/kanban?task=${a.task_id}`)} data-testid="attention-see-what-to-do">
            {t("block.seeWhatToDo")}
          </Button>
          {assistant?.available ? (
            <Button size="sm" onClick={() => assistant.open({ focus: "task", prompt: assistantPrompt(t, reason, task) })} data-testid="attention-ask-assistant">
              {t("block.askAssistantShort")}
            </Button>
          ) : null}
        </div>
      }
    >
      <p>{blockExplanation(t, reason)}</p>
      {a.dependents > 0 ? <p className="block-summary-waiting">{t("block.dependents", { n: a.dependents })}</p> : null}
    </Banner>
  );
}
